import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { api } from "../lib/api";

// Copy tables replace three-deep nested ternaries with a lookup — easier to
// scan, easier to translate, and cheap to extend when new statuses appear.
const SCRIPT_COPY = {
    paid: "you're unstoppable",
    checking: "confirming…",
};
const HEADLINE_COPY = {
    paid: "Subscription active",
    checking: "Checking payment…",
    expired: "Session expired",
};

function pickScript(status) {
    return SCRIPT_COPY[status] || "hmm";
}
function pickHeadline(status) {
    return HEADLINE_COPY[status] || "Status unknown";
}

export default function BillingSuccess() {
    const [sp] = useSearchParams();
    const sid = sp.get("session_id");
    const [status, setStatus] = useState("checking");
    // Was the user in Read-Only Mode right before this checkout? If yes we
    // celebrate a *reactivation* rather than a first-time upgrade.
    const wasReadOnly = (() => {
        try { return localStorage.getItem("vu_last_access_mode") === "read_only"; }
        catch { return false; }
    })();

    useEffect(() => {
        if (!sid) { setStatus("missing"); return; }
        let n = 0;
        const poll = async () => {
            try {
                const { data } = await api.get(`/billing/status/${sid}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    // Refresh the global Access state so ReadOnlyBanner and
                    // LockedButtons disappear immediately across every tab.
                    try {
                        window.dispatchEvent(new CustomEvent("vu:billing-refresh"));
                        localStorage.removeItem("vu_last_access_mode");
                        sessionStorage.removeItem("vu_readonly_banner_dismissed");
                    } catch (err) {
                        // Non-fatal: broadcast/localStorage may fail in some
                        // private-mode browsers — the poll still handles state.
                        console.warn("[billing-success] broadcast failed:", err);
                    }
                    return;
                }
                if (data.status === "expired") { setStatus("expired"); return; }
            } catch (err) {
                // Poll error — retry a few more times, then surface a timeout state.
                console.warn("[billing-success] poll error:", err?.message || err);
            }
            if (++n < 6) setTimeout(poll, 2000); else setStatus("timeout");
        };
        poll();
    }, [sid]);

    const paidReactivation = status === "paid" && wasReadOnly;
    const script = paidReactivation ? "welcome back" : pickScript(status);
    const headline = paidReactivation ? "Welcome back!" : pickHeadline(status);
    let body;
    if (paidReactivation) {
        body = "Your workspace has been fully restored. Everything is exactly where you left it — welcome back to Virtually Unstoppable.";
    } else if (status === "paid") {
        body = "Welcome aboard. Your free trial just became a forever-after.";
    } else {
        body = "Hold tight while we confirm with Stripe.";
    }

    return (
        <div className="min-h-screen grain grid place-items-center p-6" data-testid="billing-success-page">
            <div className="card-warm p-8 max-w-md text-center">
                {paidReactivation && (
                    <div className="mx-auto w-14 h-14 rounded-full brand-gradient text-white grid place-items-center mb-3">
                        <PartyPopper size={26} />
                    </div>
                )}
                <div className="font-script text-2xl text-[var(--brand-orange)]">{script}</div>
                <div className="font-serif text-3xl mt-1">{headline}</div>
                <p className="text-sm text-[var(--text-ink)]/70 mt-3 leading-relaxed">{body}</p>
                <Link to="/dashboard" data-testid="billing-success-continue" className="btn-primary inline-block mt-6">
                    {paidReactivation ? "Continue" : "Go to dashboard"}
                </Link>
            </div>
        </div>
    );
}
